<template>
  <div class="page-container">

    <CrmPageHeader
      title="Reportes de Importación"
      subtitle="Historial y estadísticas de importaciones"
      icon="fas fa-chart-line"
    >
      <button
        class="report-btn report-btn--icon"
        @click="recargarDatos"
        :disabled="loading"
        title="Actualizar"
      >
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
      </button>
      <router-link to="/importaciones" class="report-btn report-btn--primary">
        <i class="fas fa-file-upload"></i>
        Nueva Importación
      </router-link>
    </CrmPageHeader>

    <!-- Stat cards -->
    <div class="row g-3 mb-4">
      <div class="col-xl-3 col-sm-6">
        <div class="stat-card d-flex align-items-center gap-3">
          <div class="stat-card__icon bg-gradient-primary">
            <i class="fas fa-file-import text-white"></i>
          </div>
          <div>
            <p class="stat-card__label">Total Importaciones</p>
            <p class="stat-card__value">{{ resumen.total_importaciones || 0 }}</p>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-sm-6">
        <div class="stat-card d-flex align-items-center gap-3">
          <div class="stat-card__icon bg-gradient-success">
            <i class="fas fa-users text-white"></i>
          </div>
          <div>
            <p class="stat-card__label">Contactos Procesados</p>
            <p class="stat-card__value">{{ resumen.total_contactos_exitosos || 0 }}</p>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-sm-6">
        <div class="stat-card d-flex align-items-center gap-3">
          <div class="stat-card__icon bg-gradient-danger">
            <i class="fas fa-exclamation-triangle text-white"></i>
          </div>
          <div>
            <p class="stat-card__label">Con Errores</p>
            <p class="stat-card__value">{{ resumen.total_contactos_fallidos || 0 }}</p>
          </div>
        </div>
      </div>
      <div class="col-xl-3 col-sm-6">
        <div class="stat-card d-flex align-items-center gap-3">
          <div class="stat-card__icon bg-gradient-info">
            <i class="fas fa-chart-pie text-white"></i>
          </div>
          <div>
            <p class="stat-card__label">Tasa de Éxito</p>
            <p class="stat-card__value">{{ resumen.tasa_exito_promedio || 0 }}%</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="crm-card mb-4">
      <div class="crm-card__header">
        <p style="font-size:.82rem;font-weight:600;color:#1E293B;margin:0">Filtros</p>
      </div>
      <div class="crm-card__body">
        <div class="row g-3 align-items-end">
          <div class="col-md-3">
            <label class="filter-label">Tipo de Importación</label>
            <select v-model="localFilters.tipo" class="filter-select">
              <option value="">Todos</option>
              <option value="inmuebles-links-vistas">Inmuebles (links y vistas)</option>
              <option value="contactos-proppit">Proppit</option>
              <option value="contactos-ciencuadra">Ciencuadra</option>
              <option value="contactos-metrocuadrado">MetroCuadrado</option>
              <option value="contactos-fincaraiz">Fincaraíz</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="filter-label">Período</label>
            <select v-model="localFilters.periodo" class="filter-select" @change="handlePeriodoChange">
              <option value="hoy">Hoy</option>
              <option value="semana">Esta Semana</option>
              <option value="mes">Este Mes</option>
              <option value="personalizado">Personalizado</option>
            </select>
          </div>
          <div v-if="localFilters.periodo === 'personalizado'" class="col-md-2">
            <label class="filter-label">Desde</label>
            <input type="date" v-model="localFilters.desde" class="filter-select" />
          </div>
          <div v-if="localFilters.periodo === 'personalizado'" class="col-md-2">
            <label class="filter-label">Hasta</label>
            <input type="date" v-model="localFilters.hasta" class="filter-select" />
          </div>
          <div class="col-md-2">
            <button
              class="report-btn report-btn--primary w-100"
              @click="aplicarFiltros"
              :disabled="loading"
            >
              <i class="fas fa-filter"></i>
              Filtrar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- History table -->
    <div class="crm-card">
      <div class="crm-card__header d-flex align-items-center justify-content-between">
        <div>
          <p style="font-size:.82rem;font-weight:600;color:#1E293B;margin:0">Historial de Importaciones</p>
          <p style="font-size:.72rem;color:#94A3B8;margin:0">Registro de todas las importaciones realizadas</p>
        </div>
        <span class="badge bg-light" style="font-size:.75rem;padding:6px 12px;color:#475569">
          {{ importaciones.length }} registros
        </span>
      </div>
      <div class="crm-card__body p-0">
        <div class="table-responsive">
          <table class="crm-table">
            <thead>
              <tr>
                <th>Archivo</th>
                <th>Tipo</th>
                <th class="text-center">Estado</th>
                <th class="text-center">Total</th>
                <th class="text-center">Exitosos</th>
                <th class="text-center">Fallidos</th>
                <th class="text-center">Fecha</th>
                <th class="text-center"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="8" class="text-center py-5">
                  <div class="spinner-border text-primary" style="width:1.5rem;height:1.5rem" role="status">
                    <span class="visually-hidden">Cargando...</span>
                  </div>
                </td>
              </tr>
              <tr v-else-if="importaciones.length === 0">
                <td colspan="8" class="text-center py-5">
                  <i class="fas fa-inbox d-block mb-2" style="font-size:2rem;color:#CBD5E1"></i>
                  <span style="font-size:.82rem;color:#94A3B8">No hay importaciones registradas</span>
                </td>
              </tr>
              <tr v-else v-for="log in importaciones" :key="log.id">
                <td>
                  <div class="file-cell">
                    <div class="file-cell__icon">
                      <i class="fas fa-file-excel"></i>
                    </div>
                    <div>
                      <p class="file-cell__name">{{ log.nombre_archivo }}</p>
                      <p class="file-cell__user">Por {{ log.user?.name || 'Usuario' }}</p>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="tipo-badge">{{ formatTipo(log.tipo_importacion) }}</span>
                </td>
                <td class="text-center">
                  <span :class="getEstadoBadgeClass(log.estado)">{{ formatEstado(log.estado) }}</span>
                </td>
                <td class="text-center">
                  <span style="font-size:.8rem;font-weight:600;color:#475569">{{ log.total_filas || 0 }}</span>
                </td>
                <td class="text-center">
                  <span style="font-size:.8rem;font-weight:600;color:#059669">{{ log.procesadas_exitosas || 0 }}</span>
                </td>
                <td class="text-center">
                  <span style="font-size:.8rem;font-weight:600;color:#DC2626">{{ log.procesadas_fallidas || 0 }}</span>
                </td>
                <td class="text-center">
                  <span style="font-size:.75rem;color:#64748B">{{ formatFecha(log.created_at) }}</span>
                </td>
                <td class="text-center">
                  <button class="table-action-btn" @click="verDetalle(log)" title="Ver detalle">
                    <i class="fas fa-eye"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Detail Modal -->
    <SoftModal
      v-if="selectedLog && showDetailModal"
      :modalActive="showDetailModal"
      @close="showDetailModal = false"
      size="lg"
    >
      <template #header>
        <div style="display:flex;align-items:center;gap:10px">
          <div style="width:32px;height:32px;border-radius:8px;background:linear-gradient(135deg,#1E3A5F,#2563EB);display:flex;align-items:center;justify-content:center;color:#fff;font-size:.85rem">
            <i class="fas fa-file-import"></i>
          </div>
          <span>Detalle de Importación</span>
        </div>
      </template>

      <div>
        <!-- Info grid -->
        <div class="detail-info-grid mb-4">
          <div class="detail-info-item">
            <span class="detail-info-label">Archivo</span>
            <span class="detail-info-value">{{ selectedLog.nombre_archivo }}</span>
          </div>
          <div class="detail-info-item">
            <span class="detail-info-label">Tipo</span>
            <span class="detail-info-value">{{ formatTipo(selectedLog.tipo_importacion) }}</span>
          </div>
          <div class="detail-info-item">
            <span class="detail-info-label">Usuario</span>
            <span class="detail-info-value">{{ selectedLog.user?.name || 'N/A' }}</span>
          </div>
          <div class="detail-info-item">
            <span class="detail-info-label">Fecha</span>
            <span class="detail-info-value">{{ formatFechaCompleta(selectedLog.created_at) }}</span>
          </div>
          <div class="detail-info-item">
            <span class="detail-info-label">Duración</span>
            <span class="detail-info-value">{{ selectedLog.estadisticas?.tiempo_procesamiento || 'N/A' }}</span>
          </div>
          <div class="detail-info-item">
            <span class="detail-info-label">Estado</span>
            <span :class="getEstadoBadgeClass(selectedLog.estado)">{{ formatEstado(selectedLog.estado) }}</span>
          </div>
        </div>

        <!-- Result stats -->
        <p class="section-mini-label">Resultados</p>
        <div class="result-stats mb-4">
          <div class="result-stat">
            <i class="fas fa-list result-stat__icon" style="color:#2563EB"></i>
            <p class="result-stat__value">{{ selectedLog.total_filas }}</p>
            <p class="result-stat__label">Total Filas</p>
          </div>
          <div class="result-stat result-stat--success">
            <i class="fas fa-check result-stat__icon"></i>
            <p class="result-stat__value">{{ selectedLog.procesadas_exitosas }}</p>
            <p class="result-stat__label">Exitosas</p>
          </div>
          <div class="result-stat result-stat--error">
            <i class="fas fa-times result-stat__icon"></i>
            <p class="result-stat__value">{{ selectedLog.procesadas_fallidas }}</p>
            <p class="result-stat__label">Fallidas</p>
          </div>
        </div>

        <!-- Additional stats -->
        <div v-if="selectedLog.estadisticas">
          <p class="section-mini-label">Estadísticas Detalladas</p>
          <div class="detail-stats-table">
            <div
              v-for="(item, idx) in formatEstadisticas(selectedLog.estadisticas)"
              :key="idx"
              class="detail-stats-row"
            >
              <span class="detail-stats-key">{{ item.label }}</span>
              <span class="detail-stats-val">{{ item.value }}</span>
            </div>
          </div>
        </div>

        <!-- Errors -->
        <div v-if="selectedLog.errores && selectedLog.errores.length > 0" class="mt-4">
          <p class="section-mini-label">Errores Encontrados ({{ selectedLog.errores.length }})</p>
          <div class="table-responsive" style="max-height:280px;overflow-y:auto;border-radius:8px;border:1px solid #E2E8F0">
            <table class="table table-sm mb-0">
              <thead>
                <tr>
                  <th style="font-size:.7rem;color:#64748B;padding:8px 12px">Fila</th>
                  <th style="font-size:.7rem;color:#64748B;padding:8px 12px">Código</th>
                  <th style="font-size:.7rem;color:#64748B;padding:8px 12px">Error</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(error, index) in selectedLog.errores" :key="index">
                  <td style="font-size:.75rem;padding:7px 12px">{{ error.fila }}</td>
                  <td style="font-size:.75rem;padding:7px 12px">{{ error.codigo || error.referencia || 'N/A' }}</td>
                  <td style="font-size:.75rem;padding:7px 12px;color:#DC2626">{{ error.error }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <template #footer>
        <button class="report-btn report-btn--outline" @click="showDetailModal = false">Cerrar</button>
      </template>
    </SoftModal>

  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import CrmPageHeader from '@/components/CrmPageHeader.vue';
import SoftModal from '@/components/SoftModal.vue';

export default {
  name: 'ImportLogsReport',
  components: { CrmPageHeader, SoftModal },

  setup() {
    const store = useStore();
    const showDetailModal = ref(false);

    const localFilters = reactive({
      tipo: '',
      periodo: 'hoy',
      desde: '',
      hasta: ''
    });

    const loading = computed(() => store.getters['importaciones/getLoading']);
    const importaciones = computed(() => store.getters['importaciones/getImportaciones']);
    const resumen = computed(() => store.getters['importaciones/getResumen']);
    const selectedLog = computed(() => store.getters['importaciones/getImportacionActual']);

    const recargarDatos = () => aplicarFiltros();

    const aplicarFiltros = async () => {
      try {
        await store.dispatch('importaciones/aplicarFiltros', localFilters);
      } catch (error) {
        console.error('Error al aplicar filtros:', error);
      }
    };

    const handlePeriodoChange = () => {
      const hoy = new Date();
      if (localFilters.periodo === 'semana') {
        const inicio = new Date(hoy);
        inicio.setDate(hoy.getDate() - hoy.getDay());
        localFilters.desde = inicio.toISOString().split('T')[0];
        localFilters.hasta = hoy.toISOString().split('T')[0];
      } else if (localFilters.periodo === 'mes') {
        const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        localFilters.desde = inicio.toISOString().split('T')[0];
        localFilters.hasta = hoy.toISOString().split('T')[0];
      } else {
        localFilters.desde = '';
        localFilters.hasta = '';
      }
      aplicarFiltros();
    };

    const verDetalle = (log) => {
      store.commit('importaciones/setImportacionActual', log);
      showDetailModal.value = true;
    };

    const formatTipo = (tipo) => {
      const map = {
        'inmuebles-links-vistas': 'Inmuebles (links y vistas)',
        'contactos-portales-secundarios': 'Inmuebles (links y vistas)',
        'contactos-proppit': 'Proppit',
        proppit: 'Proppit',
        'contactos-ciencuadra': 'Ciencuadra',
        'contactos-metrocuadrado': 'MetroCuadrado',
        'contactos-fincaraiz': 'Fincaraíz'
      };
      return map[tipo] || tipo;
    };

    const formatEstado = (estado) => {
      const map = {
        procesando: 'Procesando',
        completado: 'Completado',
        error: 'Error',
        en_cola: 'En Cola'
      };
      return map[estado] || estado;
    };

    const getEstadoBadgeClass = (estado) => {
      const map = {
        procesando: 'status-badge status-badge--warning',
        completado: 'status-badge status-badge--success',
        error:      'status-badge status-badge--danger',
        en_cola:    'status-badge status-badge--info'
      };
      return map[estado] || 'status-badge status-badge--default';
    };

    const formatFecha = (fecha) => {
      if (!fecha) return 'N/A';
      return new Date(fecha).toLocaleDateString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    };

    const formatFechaCompleta = (fecha) => {
      if (!fecha) return 'N/A';
      return new Date(fecha).toLocaleDateString('es-ES', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    };

    const formatEstadisticas = (stats) => {
      const labels = {
        tasa_exito: 'Tasa de Éxito',
        tiempo_procesamiento: 'Tiempo de Procesamiento',
        clientes_nuevos: 'Clientes Nuevos',
        clientes_actualizados: 'Clientes Actualizados',
        contactos_registrados: 'Contactos Registrados',
        codigos_encontrados: 'Códigos Encontrados',
        codigos_no_encontrados: 'Códigos No Encontrados',
        inmuebles_encontrados: 'Inmuebles Encontrados',
        inmuebles_no_encontrados: 'Inmuebles No Encontrados',
        portales_procesados: 'Portales Procesados',
        portales_exitosos: 'Portales Exitosos',
        ciudades_encontradas: 'Ciudades Encontradas'
      };
      return Object.entries(stats)
        .filter(([key]) => labels[key] && key !== 'detalle_por_portal')
        .map(([key, value]) => ({
          label: labels[key],
          value: key === 'tasa_exito' ? `${value}%` : value
        }));
    };

    onMounted(() => {
      store.dispatch('importaciones/obtenerReporteDiario');
    });

    return {
      loading,
      importaciones,
      selectedLog,
      localFilters,
      resumen,
      showDetailModal,
      recargarDatos,
      aplicarFiltros,
      handlePeriodoChange,
      verDetalle,
      formatTipo,
      formatEstado,
      getEstadoBadgeClass,
      formatFecha,
      formatFechaCompleta,
      formatEstadisticas
    };
  }
};
</script>

<style scoped>
/* Buttons */
.report-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: .78rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all .15s ease;
  text-decoration: none;
}
.report-btn--primary {
  background: linear-gradient(135deg,#1E3A5F,#2563EB);
  color: #fff;
  box-shadow: 0 3px 10px rgba(37,99,235,0.25);
}
.report-btn--primary:hover:not(:disabled) {
  box-shadow: 0 5px 16px rgba(37,99,235,0.35);
  transform: translateY(-1px);
}
.report-btn--primary:disabled { opacity: .6; cursor: not-allowed; transform: none; }

.report-btn--outline {
  background: transparent;
  color: #475569;
  border: 1.5px solid #E2E8F0;
}
.report-btn--outline:hover { border-color: #2563EB; color: #2563EB; background: #EFF6FF; }

.report-btn--icon {
  background: #fff;
  color: #475569;
  border: 1.5px solid #E2E8F0;
  padding: 8px 10px;
}
.report-btn--icon:hover:not(:disabled) { border-color: #2563EB; color: #2563EB; background: #EFF6FF; }
.report-btn--icon:disabled { opacity: .5; cursor: not-allowed; }

.w-100 { width: 100%; justify-content: center; }

/* Filters */
.filter-label {
  display: block;
  font-size: .72rem;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: .05em;
  margin-bottom: 6px;
}

.filter-select {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #E2E8F0;
  border-radius: 8px;
  font-size: .8rem;
  color: #1E293B;
  background: #F8FAFC;
  outline: none;
  transition: border-color .15s ease, box-shadow .15s ease;
  appearance: none;
  -webkit-appearance: none;
}
.filter-select:focus {
  border-color: #2563EB;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
}

/* Table */
.crm-table {
  width: 100%;
  border-collapse: collapse;
}
.crm-table th {
  padding: 10px 16px;
  font-size: .68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .07em;
  color: #64748B;
  background: #F8FAFC;
  border-bottom: 1px solid #E2E8F0;
  white-space: nowrap;
}
.crm-table td {
  padding: 12px 16px;
  font-size: .82rem;
  color: #475569;
  border-bottom: 1px solid #F1F5F9;
  vertical-align: middle;
}
.crm-table tbody tr:last-child td { border-bottom: none; }
.crm-table tbody tr:hover td { background: #EFF6FF; }

/* File cell */
.file-cell { display: flex; align-items: center; gap: 10px; }
.file-cell__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #DCFCE7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: .85rem;
  color: #16A34A;
  flex-shrink: 0;
}
.file-cell__name { font-size: .8rem; font-weight: 600; color: #1E293B; margin: 0; }
.file-cell__user { font-size: .7rem; color: #94A3B8; margin: 0; }

/* Type badge */
.tipo-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: .68rem;
  font-weight: 600;
  background: #EFF6FF;
  color: #2563EB;
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: .68rem;
  font-weight: 600;
}
.status-badge--success { background: #ECFDF5; color: #059669; }
.status-badge--warning { background: #FFFBEB; color: #D97706; }
.status-badge--danger  { background: #FEF2F2; color: #DC2626; }
.status-badge--info    { background: #E0F2FE; color: #0284C7; }
.status-badge--default { background: #F1F5F9; color: #64748B; }

/* Table action button */
.table-action-btn {
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: #94A3B8;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: .85rem;
  transition: all .15s;
}
.table-action-btn:hover { background: #EFF6FF; color: #2563EB; }

/* Detail modal */
.detail-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
.detail-info-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 14px;
  background: #F8FAFC;
  border-radius: 8px;
  border: 1px solid #E2E8F0;
}
.detail-info-label { font-size: .68rem; font-weight: 700; color: #94A3B8; text-transform: uppercase; letter-spacing: .06em; }
.detail-info-value { font-size: .82rem; font-weight: 500; color: #1E293B; }

.result-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.result-stat {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 16px 12px;
  text-align: center;
}
.result-stat--success { background: #ECFDF5; border-color: #A7F3D0; }
.result-stat--error   { background: #FEF2F2; border-color: #FECACA; }

.result-stat__icon { font-size: 1.2rem; display: block; margin-bottom: 8px; }
.result-stat--success .result-stat__icon { color: #059669; }
.result-stat--error   .result-stat__icon { color: #DC2626; }

.result-stat__value { font-size: 1.4rem; font-weight: 800; color: #1E293B; margin: 0 0 2px; }
.result-stat--success .result-stat__value { color: #059669; }
.result-stat--error   .result-stat__value { color: #DC2626; }

.result-stat__label {
  font-size: .7rem;
  color: #64748B;
  margin: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .05em;
}

.detail-stats-table {
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  overflow: hidden;
}
.detail-stats-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 14px;
  border-bottom: 1px solid #F1F5F9;
}
.detail-stats-row:last-child { border-bottom: none; }
.detail-stats-row:nth-child(even) { background: #F8FAFC; }
.detail-stats-key { font-size: .78rem; color: #64748B; }
.detail-stats-val { font-size: .78rem; font-weight: 700; color: #1E293B; }

.section-mini-label {
  font-size: .7rem;
  font-weight: 700;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 8px;
}
</style>
